import {Button, Dialog, DotProgress, IconButton} from '@wandb/components';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React from 'react';

import {useReproduceRunDrawerQuery} from '../../generated/graphql';
import {propagateErrorsContext} from '../../util/errors';
import {useNavbarBottom} from '../../util/hooks/useNavbarBottom';
import {ReproduceRun} from './ReproduceRun';

type HeaderProps = {
  onClose(): void;
};
function Header({onClose}: HeaderProps) {
  return (
    <div className="flex flex-row items-center justify-between border-b border-wb-border px-20 py-16">
      <h2 className="text-wb-text-primary text-xl font-semibold">
        Reproduce this run
      </h2>
      <IconButton
        icon="close"
        aria-label="Close"
        onPress={onClose}
        variant="ghost"
      />
    </div>
  );
}

type ContentsProps = {
  entityName: string;
  isOpen: boolean;
  onClose(): void;
  projectName: string;
  runName: string;
};
function Contents({
  entityName,
  isOpen,
  onClose,
  projectName,
  runName,
}: ContentsProps) {
  const {data, loading, error, refetch} = useReproduceRunDrawerQuery({
    context: propagateErrorsContext(),
    variables: {
      entityName,
      projectName,
      runName,
    },
    skip: !isOpen,
  });
  const run = data?.project?.run;

  if (error) {
    return (
      <div className="flex flex-col gap-16">
        <p>Could not fetch run reproducability information!</p>
        <Button
          startIcon="reload-refresh"
          variant="secondary"
          onPress={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  } else if (loading) {
    return (
      <div className="py-32">
        <DotProgress aria-label="Loading run info" size="lg" />
      </div>
    );
  } else if (run) {
    return <ReproduceRun onClose={onClose} run={run} />;
  }
  return null;
}

type ReproduceRunDrawerProps = {
  entityName: string;
  isOpen: boolean;
  onOpenChange(open: boolean): void;
  projectName: string;
  runName: string;
};

export function ReproduceRunDrawer({
  entityName,
  isOpen,
  onOpenChange,
  projectName,
  runName,
}: ReproduceRunDrawerProps) {
  const topPadding = useNavbarBottom();

  return (
    <Dialog.Drawer
      aria-label="Reproduce this run"
      backdrop={false}
      isOpen={isOpen}
      offsetTop={topPadding}
      onOpenChange={onOpenChange}>
      {({close}) => (
        <TailwindContents>
          <Header onClose={close} />
          <Dialog.Content>
            <div className="px-20 py-16">
              <Contents
                entityName={entityName}
                isOpen={isOpen}
                onClose={close}
                projectName={projectName}
                runName={runName}
              />
            </div>
          </Dialog.Content>
        </TailwindContents>
      )}
    </Dialog.Drawer>
  );
}
